import {useMemo} from "react";
import type {
    ChecklistMetadata,
    ChecklistToc,
    CollectionDataResponse,
    LabelGroup,
    Status,
} from "../types";
import {ConfirmCheckAllModal} from "./modals/ConfirmCheckAllModal";
import {DeleteFillingModal} from "./modals/DeleteFillingModal";
import {DuplicateFillingModal} from "./modals/DuplicateFillingModal";
import {FillingFormModal} from "./modals/FillingFormModal";

interface ChecklistModalsProps {
    collectionData?: CollectionDataResponse;
    viewFilteredToc: ChecklistToc;
    effectiveRootNodeId: string;
    checklistConfig: {
        metadata: ChecklistMetadata;
        viewGroups: LabelGroup[];
        labelData: Record<string, string>;
        defaultCollectionId: string;
    };
    isEditFillingOpen: boolean;
    isCreateFillingOpen: boolean;
    isOpenFillingOpen: boolean;
    isShareFillingOpen: boolean;
    isDuplicateFillingOpen: boolean;
    isDeleteFillingOpen: boolean;
    confirmCheckAllData?: {status: Status; leafNodeIds: string[]};
    closeModal: () => void;
    handleConfirmCheckAll: () => void;
    handlePatchDimensions: (dimensions: string[]) => Promise<void>;
    handlePatchCollectionName: (name: string) => Promise<void>;
    onCollectionChange: () => void;
    isPatchCollectionNameLoading: boolean;
    isPatchDimensionsLoading: boolean;
    infoIds: string[];
    leafNodeIds: string[];
}

export const ChecklistModals = ({
    collectionData,
    viewFilteredToc,
    effectiveRootNodeId,
    checklistConfig,
    isEditFillingOpen,
    isCreateFillingOpen,
    isOpenFillingOpen,
    isDuplicateFillingOpen,
    isDeleteFillingOpen,
    confirmCheckAllData,
    closeModal,
    handleConfirmCheckAll,
    handlePatchDimensions,
    handlePatchCollectionName,
    onCollectionChange,
    isPatchCollectionNameLoading,
    isPatchDimensionsLoading,
    infoIds,
    leafNodeIds,
}: ChecklistModalsProps) => {

    const topicMeta = {
        topictypeId: checklistConfig.metadata.topictypeId,
        name: checklistConfig.metadata.name,
        topicId: checklistConfig.metadata.topicId,
    };

    const hasOnlyInfoNodes = useMemo(() => {
        const viewFilteredLeafIds = leafNodeIds.filter(
            (id) => viewFilteredToc[id],
        );
        if (viewFilteredLeafIds.length === 0) return true;
        const infoIdSet = new Set(infoIds);
        return viewFilteredLeafIds.every((id) => infoIdSet.has(id));
    }, [viewFilteredToc, leafNodeIds, infoIds]);

    if (!effectiveRootNodeId || !viewFilteredToc[effectiveRootNodeId]) {
        return null;
    }

    const fillingFormProps = {
        topicMeta,
        defaultCollectionId: checklistConfig.defaultCollectionId,
        viewGroups: checklistConfig.viewGroups,
        labelData: checklistConfig.labelData,
        collectionData,
        onPatchDimensions: handlePatchDimensions,
        onPatchCollectionName: handlePatchCollectionName,
        isPatchCollectionNameLoading,
        isPatchDimensionsLoading,
        onCollectionChange,
    };

    return (
        <>
            <ConfirmCheckAllModal
                opened={confirmCheckAllData !== undefined}
                onClose={closeModal}
                onConfirm={handleConfirmCheckAll}
                status={confirmCheckAllData?.status ?? 0}
            />

            <FillingFormModal
                opened={isEditFillingOpen}
                onClose={closeModal}
                mode="edit"
                {...fillingFormProps}
            />

            <FillingFormModal
                opened={isCreateFillingOpen}
                onClose={closeModal}
                mode="create"
                {...fillingFormProps}
            />

            <FillingFormModal
                opened={isOpenFillingOpen}
                onClose={closeModal}
                mode="open"
                {...fillingFormProps}
            />

            {/* ShareFillingModal deferred — needs accessPermissions state + 2 new endpoints */}

            {isDuplicateFillingOpen && collectionData && (
                <DuplicateFillingModal
                    opened
                    onClose={closeModal}
                    collectionId={collectionData.metadata.collectionId}
                    collectionName={collectionData.metadata.name}
                    topicMeta={topicMeta}
                    hasOnlyInfoNodes={hasOnlyInfoNodes}
                />
            )}

            {isDeleteFillingOpen && collectionData && (
                <DeleteFillingModal
                    opened
                    onClose={closeModal}
                    collectionId={collectionData.metadata.collectionId}
                    collectionName={collectionData.metadata.name}
                    topicMeta={topicMeta}
                />
            )}
        </>
    );
};
